import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, BellIcon } from '@heroicons/react/24/outline';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import roLocale from 'date-fns/locale/ro'; // Optional: for Romanian locale
import axios from 'axios';
import { auth } from '../../../firebase';

const NotificationsModal = ({ isOpen, onClose, selectedCar }) => {
  const { t } = useTranslation();
  // Store original body styles and scroll position
  const bodyStyles = useRef({});
  
  // State for each notification type
  const [rcaDate, setRcaDate] = useState('');
  const [itpDate, setItpDate] = useState('');
  const [cascoDate, setCascoDate] = useState('');
  const [rovinietaDate, setRovinietaDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');
      
      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';
      
      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }
      
      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);

  // Return null if the modal is not open
  if (!isOpen || !selectedCar) return null;
  
  // Handler for saving notifications
  const handleSaveNotifications = async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      const idToken = await auth.currentUser?.getIdToken();
      
      // Create notification payload with all dates
      const notificationPayload = {
        registrationNumber: selectedCar.plate,
        rca: rcaDate || null,
        inspectionDue: itpDate || null,
        vigneta: rovinietaDate || null,
        casco: cascoDate || null
      };
      
      console.log('Notification payload to save:', notificationPayload);
      
      // Save all notifications in a single request
      await axios.post(
        'https://api.servicegest.ro/driver/car/notification',
        notificationPayload,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      alert(t('notifications.saveSuccess'));
      onClose();
      
    } catch (err) {
      console.error('Error saving notifications:', err);
      setError(err.response?.data?.message || t('notifications.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for deleting a specific notification
  const handleDeleteNotification = async (notificationType) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      
      await axios.post(
        'https://api.servicegest.ro/driver/car/deleteNotification',
        {
          type: notificationType,
          registrationNumber: selectedCar.plate
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      // Clear the local state for the deleted notification
      switch (notificationType) {
        case 'RCA':
          setRcaDate('');
          break;
        case 'ITP':
          setItpDate('');
          break;
        case 'CASCO':
          setCascoDate('');
          break;
        case 'ROVINIETA':
          setRovinietaDate('');
          break;
      }
      
      alert(t('notifications.deleteSuccess', { type: notificationType }));
      
    } catch (err) {
      console.error('Error deleting notification:', err);
      alert(err.response?.data?.message || t('notifications.deleteError'));
    }
  };

  // Parse date string to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    } catch (e) {
      console.error('Date parsing error:', e);
      return null;
    }
  };

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    if (!date) return '';
    try {
      return date.toISOString().split('T')[0];
    } catch (e) {
      console.error('Date formatting error:', e);
      return '';
    }
  };

  // Calculate min date (today)
  const today = new Date();

  // Create custom dark mode theme for MUI components
  const darkTheme = createTheme({
    palette: {
      mode: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      primary: {
        main: '#3b82f6', // purple-500
      },
      background: {
        paper: document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff', // gray-800 : white
      },
      text: {
        primary: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827', // gray-100 : gray-900
      }
    },
  });

  // DatePicker component with dark mode support
  const DarkModeDatePicker = ({ label, value, onChange, minDate, disabled }) => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        minDate={minDate}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            className: "mt-1",
            variant: "outlined",
            sx: {
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? 'rgb(31, 41, 55)' : 'transparent', // gray-800 in dark mode
                color: isDarkMode ? 'rgb(243, 244, 246)' : 'inherit', // gray-100 in dark mode
                '& fieldset': {
                  borderColor: isDarkMode ? 'rgb(75, 85, 99)' : 'rgb(209, 213, 219)', // gray-600 : gray-300
                },
                '&:hover fieldset': {
                  borderColor: 'rgb(59, 130, 246)', // purple-500
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgb(59, 130, 246)', // purple-500
                },
              },
              '& .MuiInputLabel-root': {
                color: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)', // gray-400 : gray-500
              },
              '& .MuiIconButton-root': {
                color: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)', // gray-400 : gray-500
              },
            }
          }
        }}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full mt-0 md:mt-20 max-w-5xl my-8 mb-20 md:max-h-[calc(80vh-2rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
                <BellIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                  {t('notifications.title', { plate: selectedCar ? selectedCar.plate : '' })}
                </h3>
                {/* <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('notifications.subtitle')}
                </p> */}
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-2 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          
          ) : (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={roLocale}>
              <ThemeProvider theme={darkTheme}>
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                  <div className="bg-slate-50 dark:bg-slate-700 dark:bg-opacity-30 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="grid grid-cols-1 gap-4">
                      {/* RCA Notification */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            {t('notifications.rcaExpiry')}
                          </label>
                          {rcaDate && (
                            <button
                              onClick={() => handleDeleteNotification('RCA')}
                              className="text-xs text-rose-600 dark:text-rose-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                              {t('notifications.delete')}
                            </button>
                          )}
                        </div>
                        <DarkModeDatePicker
                          value={parseDate(rcaDate)}
                          onChange={(newDate) => setRcaDate(formatDateForAPI(newDate))}
                          minDate={today}
                          disabled={isSaving}
                        />
                      </div>

                      {/* ITP Notification */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            {t('notifications.itpExpiry')}
                          </label>
                          {itpDate && (
                            <button
                              onClick={() => handleDeleteNotification('ITP')}
                              className="text-xs text-rose-600 dark:text-rose-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                              {t('notifications.delete')}
                            </button>
                          )}
                        </div>
                        <DarkModeDatePicker
                          value={parseDate(itpDate)}
                          onChange={(newDate) => setItpDate(formatDateForAPI(newDate))}
                          minDate={today}
                          disabled={isSaving}
                        />
                      </div>

                                            {/* CASCO Notification */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            {t('notifications.cascoExpiry')}
                          </label>
                          {cascoDate && (
                            <button
                              onClick={() => handleDeleteNotification('CASCO')}
                              className="text-xs text-rose-600 dark:text-rose-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                              {t('notifications.delete')}
                            </button>
                          )}
                        </div>
                        <DarkModeDatePicker
                          value={parseDate(cascoDate)}
                          onChange={(newDate) => setCascoDate(formatDateForAPI(newDate))}
                          minDate={today}
                          disabled={isSaving}
                        />
                      </div>

                      {/* Rovinieta Notification */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            {t('notifications.rovinietaExpiry')}
                          </label>
                          {rovinietaDate && (
                            <button
                              onClick={() => handleDeleteNotification('ROVINIETA')}
                              className="text-xs text-rose-600 dark:text-rose-400 hover:text-red-800 dark:hover:text-red-300"
                            >
                              {t('notifications.delete')}
                            </button>
                          )}
                        </div>
                        <DarkModeDatePicker
                          value={parseDate(rovinietaDate)}
                          onChange={(newDate) => setRovinietaDate(formatDateForAPI(newDate))}
                          minDate={today}
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </ThemeProvider>
            </LocalizationProvider>
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3 rounded-b-xl flex-shrink-0">
          <button
            type="button"
            className="bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('common.close')}
          </button>
          <button
            type="button"
            className={`inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white ${
              isSaving ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800`}
            onClick={handleSaveNotifications}
            disabled={isSaving || isLoading}
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('notifications.saving')}
              </>
            ) : (
              t('common.save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;
